import React, { useEffect } from 'react'

import Select from '@/components/select/Select'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { useReferralStatusesOptions } from '@/hooks/useReferralStatusesOptions'
import {
  API_PATH_INTERNAL,
  REFERRAL_IMPORT_DIRECTION,
  REFERRAL_STAGE_CODE,
} from '@/lib/constants'
import { cn } from '@/lib/utils'
import {
  selectLogHistoricalReferralState,
  updateLogData,
} from '@/redux/slices/logHistoricalReferral'

interface Props {
  className?: string
  disabled?: boolean
}

const SelectStatus: React.FC<Props> = ({ className, disabled = false }) => {
  const dispatch = useAppDispatch()
  const referralLogHistoricalState = useAppSelector(
    selectLogHistoricalReferralState,
  )
  const toCompany =
    referralLogHistoricalState.logData.referralDirection ===
    REFERRAL_IMPORT_DIRECTION.TO
      ? referralLogHistoricalState.logData.otherCompany?.id || null
      : referralLogHistoricalState.selectedUser?.company?.id || null
  const statusIri = referralLogHistoricalState.logData.referralStatus
    ? `${API_PATH_INTERNAL}/referral-mapped-statuses/${referralLogHistoricalState.logData.referralStatus.id}`
    : undefined
  const { options, statusesMap, defaultStageStatusMap, isLoading } =
    useReferralStatusesOptions(toCompany)

  useEffect(() => {
    if (!statusIri && defaultStageStatusMap.has(REFERRAL_STAGE_CODE.not_sure)) {
      // set a default "not_sure" when no status is selected
      dispatch(
        updateLogData({
          referralStatus: defaultStageStatusMap.get(
            REFERRAL_STAGE_CODE.not_sure,
          ),
        }),
      )
    } else if (
      statusIri &&
      referralLogHistoricalState.logData.referralStatus?.stage &&
      !statusesMap.has(statusIri) &&
      defaultStageStatusMap.has(
        referralLogHistoricalState.logData.referralStatus.stage,
      )
    ) {
      // track options list change (when company changes) and find similar status based on stage
      dispatch(
        updateLogData({
          referralStatus: defaultStageStatusMap.get(
            referralLogHistoricalState.logData.referralStatus.stage,
          ),
        }),
      )
    }
  }, [
    statusIri,
    referralLogHistoricalState.logData.referralStatus,
    defaultStageStatusMap,
    statusesMap,
    dispatch,
  ])

  return (
    <div className={cn('relative', className)}>
      <Select
        isDisabled={disabled}
        isLoading={isLoading}
        placeholder="Select status..."
        options={options}
        onChange={(selectedOption) =>
          dispatch(
            updateLogData({
              referralStatus:
                selectedOption && statusesMap.has(selectedOption.value)
                  ? statusesMap.get(selectedOption.value)
                  : defaultStageStatusMap.get('not_sure'),
            }),
          )
        }
        value={options.find((option) => option.value == statusIri)}
      />
    </div>
  )
}

export default SelectStatus
